import React, { useCallback, useEffect, useState } from 'react';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { episodeAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const fmtErr = (d, fb) => { if (!d) return fb; if (typeof d === 'string') return d; if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join('; '); return d.msg || d.message || JSON.stringify(d); };

const STAGES = ['REGISTERED', 'ASSESSMENT_REQUESTED', 'INITIAL_ASSESSMENT_COMPLETED',
  'CARE_PLAN_CREATED', 'ACTIVE_TREATMENT', 'ONGOING_MONITORING', 'GOALS_ACHIEVED',
  'DISCHARGE_REVIEW', 'DISCHARGED', 'FOLLOW_UP'];

const RECOVERY_COLOR = {
  NEW: '#a99aa0', ASSESSMENT_PENDING: '#a78bfa', CARE_PLAN_ACTIVE: '#60a5fa',
  IN_TREATMENT: '#38bdf8', IMPROVING: '#34d399', STABLE: '#10b981',
  AT_RISK: '#ef4444', GOALS_ACHIEVED: '#22c55e', READY_FOR_DISCHARGE: '#16a34a',
  DISCHARGED: '#8b7681', FOLLOW_UP: '#94335a', REOPENED: '#f59e0b',
};

const card = { background: '#fff', border: '1px solid #ece3e7', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 2px rgba(13,27,46,0.04)' };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 5 };

function GoalBar({ g }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, color: '#2b1b22' }}>
          {g.achieved ? '✅ ' : ''}{g.description}
        </span>
        <span style={{ fontWeight: 700, color: g.achieved ? '#16a34a' : '#475569' }}>{g.progress_pct}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 999, background: '#f1f5f9', overflow: 'hidden' }}>
        <div style={{
          width: `${g.progress_pct}%`, height: '100%', borderRadius: 999,
          background: g.achieved ? '#22c55e' : 'linear-gradient(90deg,#38bdf8,#7b1e3c)',
          transition: 'width .4s',
        }} />
      </div>
    </div>
  );
}

export default function Episodes() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING', 'QA'].includes(r));
  const canDischarge = roles.includes('DOCTOR') || roles.includes('ADMIN');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [df, setDf] = useState({});         // discharge form
  const [showDischarge, setShowDischarge] = useState(false);
  const [dErr, setDErr] = useState('');
  const [dBusy, setDBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    episodeAPI.list({ per_page: 100 })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = (ep) => {
    episodeAPI.get(ep.episode_id).then(r => setDetail(r?.data ?? r)).catch(() => {});
  };

  const doDischarge = async (e) => {
    e.preventDefault(); setDBusy(true); setDErr('');
    try {
      const r = await episodeAPI.discharge(detail.episode_id, {
        final_diagnosis: df.final_diagnosis || undefined,
        care_provided: df.care_provided || undefined,
        outcomes: df.outcomes || undefined,
        follow_up_instructions: df.follow_up || undefined,
        override_reason: df.override_reason || undefined,
      });
      setDetail(r?.data ?? r);
      setShowDischarge(false);
      load();
    } catch (err) { setDErr(fmtErr(err.response?.data?.detail, 'Discharge failed')); }
    finally { setDBusy(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>
          {isPatientOnly ? 'My Care Episodes' : 'Episodes of Care'}
        </h1>
        <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
          {isPatientOnly
            ? 'Your clinical journeys from admission to discharge.'
            : 'The clinical spine — every request, visit, report and invoice belongs to an episode.'}
        </p>
      </div>

      {loading ? <div style={{ color: '#8b7681', padding: 20 }}>Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
          {rows.length === 0 && <div style={{ color: '#a99aa0', fontSize: 13 }}>No episodes yet — they are admitted automatically when a care request is submitted.</div>}
          {rows.map(ep => {
            const achieved = ep.goals.filter(g => g.achieved).length;
            return (
              <div key={ep.episode_id} style={{ ...card, cursor: 'pointer' }} onClick={() => openDetail(ep)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#7b1e3c', letterSpacing: '0.5px' }}>{ep.episode_number}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#2b1b22', marginTop: 2 }}>
                      {isPatientOnly ? (ep.diagnosis || ep.service_type || 'Care episode') : ep.patient_name}
                    </div>
                    {!isPatientOnly && <div style={{ fontSize: 12, color: '#8b7681' }}>{ep.diagnosis || ep.admission_reason || ep.service_type || '—'}</div>}
                  </div>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999,
                    color: '#fff', background: RECOVERY_COLOR[ep.recovery_status] || '#a99aa0',
                  }}>{(ep.recovery_status || '').replace(/_/g, ' ')}</span>
                </div>
                <div style={{ fontSize: 11.5, color: '#8b7681', marginBottom: 10 }}>
                  <Badge value={ep.status} />{' '}
                  · Dr {ep.primary_doctor_name || '—'} · Nurse {ep.primary_nurse_name || '—'}
                  {ep.start_date && <> · since {new Date(ep.start_date).toLocaleDateString()}</>}
                </div>
                {ep.goals.length > 0 && (
                  <div style={{ fontSize: 11.5, color: '#475569', fontWeight: 600 }}>
                    Goals {achieved}/{ep.goals.length}
                    <div style={{ height: 6, borderRadius: 999, background: '#f1f5f9', marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${ep.goals.length ? Math.round(100 * achieved / ep.goals.length) : 0}%`, height: '100%', background: '#22c55e' }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Episode detail ── */}
      {detail && (
        <Modal title={`${detail.episode_number} — ${detail.patient_name || ''}`} onClose={() => setDetail(null)} fullscreen>
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
            {/* lifecycle stepper */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
              {STAGES.map(st => {
                const reached = (detail.status_history || []).some(h => h.status === st) || detail.status === st;
                const current = detail.status === st;
                return (
                  <span key={st} style={{
                    fontSize: 10, fontWeight: 700, padding: '4px 9px', borderRadius: 999,
                    background: current ? '#7b1e3c' : reached ? '#f6d6e6' : '#f1f5f9',
                    color: current ? '#fff' : reached ? '#7b1e3c' : '#a99aa0',
                  }}>{st.replace(/_/g, ' ')}</span>
                );
              })}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <div style={card}>
                <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 800, color: '#2b1b22' }}>Clinical summary</h3>
                {[['Admission reason', detail.admission_reason],
                  ['Diagnosis', detail.diagnosis],
                  ['Primary doctor', detail.primary_doctor_name],
                  ['Primary nurse', detail.primary_nurse_name],
                  ['Started', detail.start_date],
                  ['Expected recovery', detail.expected_recovery_date],
                  ['Discharged', detail.discharge_date],
                ].map(([k, v]) => v ? (
                  <div key={k} style={{ display: 'flex', gap: 8, fontSize: 12.5, padding: '5px 0', borderBottom: '1px solid #f8fafc' }}>
                    <span style={{ color: '#8b7681', minWidth: 130 }}>{k}</span>
                    <span style={{ fontWeight: 600, color: '#2b1b22' }}>{String(v)}</span>
                  </div>
                ) : null)}
              </div>
              <div style={card}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 800, color: '#2b1b22' }}>Clinical goals</h3>
                {detail.goals.length === 0
                  ? <div style={{ fontSize: 12.5, color: '#a99aa0' }}>No goals defined yet — the doctor sets them on the care plan.</div>
                  : detail.goals.map(g => <GoalBar key={g.goal_id} g={g} />)}
              </div>
            </div>

            {detail.discharge_summary && (
              <div style={{ ...card, marginTop: 18, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 800, color: '#14532d' }}>📄 Discharge summary</h3>
                {[['Final diagnosis', detail.discharge_summary.final_diagnosis],
                  ['Care provided', detail.discharge_summary.care_provided],
                  ['Outcomes', detail.discharge_summary.outcomes],
                  ['Follow-up', detail.discharge_summary.follow_up_instructions],
                ].map(([k, v]) => v ? (
                  <div key={k} style={{ fontSize: 12.5, marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, color: '#166534' }}>{k}: </span>
                    <span style={{ color: '#14532d' }}>{v}</span>
                  </div>
                ) : null)}
              </div>
            )}

            {canDischarge && detail.status !== 'DISCHARGED' && (
              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowDischarge(true); setDf({}); setDErr(''); }} style={{
                  padding: '10px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
                  background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700,
                }}>🏁 Discharge review…</button>
              </div>
            )}

            {showDischarge && (
              <form onSubmit={doDischarge} style={{ ...card, marginTop: 14 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 800 }}>Discharge review</h3>
                <p style={{ fontSize: 12, color: '#8b7681', margin: '0 0 12px' }}>
                  Requires every clinical goal achieved — or an explicit, documented override reason.
                </p>
                {dErr && <div style={{ background: '#fdecea', color: '#b71c1c', padding: '9px 12px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{dErr}</div>}
                {[['final_diagnosis', 'Final diagnosis'], ['care_provided', 'Care provided'],
                  ['outcomes', 'Outcomes'], ['follow_up', 'Follow-up instructions'],
                  ['override_reason', 'Override reason (only if goals unmet)']].map(([k, label]) => (
                  <div key={k} style={{ marginBottom: 10 }}>
                    <label style={lbl}>{label}</label>
                    <input style={inp} value={df[k] || ''} onChange={e => setDf(f => ({ ...f, [k]: e.target.value }))} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowDischarge(false)} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #ece3e7', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <button type="submit" disabled={dBusy} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    {dBusy ? 'Discharging…' : 'Confirm discharge'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
